import { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../lib/analytics';
import {
  answeredCount,
  isAssessmentComplete,
  type SelfAssessmentAnswers,
} from '../lib/selfAssessment';
import { SelfAssessmentForm } from './SelfAssessmentForm';

const CALENDLY_URL = 'https://calendar.app.google/C5ZTChB337egaE4Q7';
const CONTACT_EMAIL = 'info@everydaysecurity.ai';

// The backend's own scan budget is 20s (internal/handlers/risk_review.go);
// this client-side ceiling is a little above that plus scoring/network
// overhead, staying safely under API Gateway's hard 29s limit.
const FETCH_TIMEOUT_MS = 28_000;

const STEPS = [
  { title: 'Scanning the website', desc: 'Looking at the homepage, and if we can find them, the About, Team, and Contact pages.' },
  { title: 'Analyzing exposure', desc: 'Checking for named leadership, staff listings, and public contact details.' },
  { title: 'Scoring risk', desc: 'Turning findings into an authority, SMS, and email exposure score.' },
] as const;

// How long (ms) the progress UI waits before auto-advancing to each step.
// There's no server-sent progress channel — the scan is one bounded
// synchronous request — so this paces the reference-pattern step UI against
// the scan's typical real timing rather than a disconnected fake bar.
const STEP_ADVANCE_MS = [0, 6_000, 14_000];

type Tier = 'unknown' | 'low' | 'medium' | 'high' | 'critical';

interface RiskReviewResult {
  target: string;
  domain: string;
  reachable: boolean;
  tier: Tier;
  clean: boolean;
  authority_score?: number;
  sms_score?: number;
  email_score?: number;
  overall_score?: number;
  named_executives: number;
  staff_with_phone: number;
  staff_with_email: number;
  phone_numbers_found: boolean;
  email_format_predictable: boolean;
}

interface RoadmapItemResult {
  dimension: string;
  title: string;
  steps: string[];
}

interface AdvocacyCategoryResult {
  title: string;
  desc: string;
}

interface FullReportResult {
  target: string;
  quadrant: string;
  quadrant_label: string;
  preparedness_score: number;
  preparedness_a: number;
  preparedness_b: number;
  preparedness_c: number;
  preparedness_d: number;
  roadmap_framing?: string;
  roadmap_items: RoadmapItemResult[];
  baseline_advocacy: AdvocacyCategoryResult[];
  cost_has_history: boolean;
  cost_total_low?: number;
  cost_total_high?: number;
  cost_total_open?: boolean;
  cost_single_low?: number;
  cost_single_high?: number;
  cost_single_open?: boolean;
  resolution_time: string;
  perception_gap_note?: string;
  report_filename: string;
  report_pdf_base64: string;
}

type PageState = 'input' | 'in_progress' | 'report';
type ScanStatus = 'scanning' | 'done' | 'error';
type FullReportStatus = 'idle' | 'loading' | 'done' | 'error';

const TIER_COPY: Record<Tier, { label: string; className: string }> = {
  unknown: { label: 'Unknown', className: 'tier-unknown' },
  low: { label: 'Low risk', className: 'tier-low' },
  medium: { label: 'Medium risk', className: 'tier-medium' },
  high: { label: 'High risk', className: 'tier-high' },
  critical: { label: 'Critical risk', className: 'tier-critical' },
};

function exposureLevel(score: number): { label: string; className: string } {
  if (score < 25) return { label: 'Low', className: 'tier-low' };
  if (score < 50) return { label: 'Medium', className: 'tier-medium' };
  if (score < 75) return { label: 'High', className: 'tier-high' };
  return { label: 'Critical', className: 'tier-critical' };
}

// Preparedness is the INVERSE of exposure — high is good. Reuses the same
// tier-low/medium/high CSS classes purely for their colors (green/amber/
// red), not their exposure semantics.
function preparednessLevel(score: number): { label: string; className: string } {
  if (score < 2.33) return { label: 'Needs work', className: 'tier-high' };
  if (score < 3.67) return { label: 'Developing', className: 'tier-medium' };
  return { label: 'Strong', className: 'tier-low' };
}

const QUADRANTS = [
  { key: 'low_exposure_high_preparedness', label: 'Best position', row: 1, col: 1 },
  { key: 'high_exposure_high_preparedness', label: 'Exposed, managing it', row: 1, col: 2 },
  { key: 'low_exposure_low_preparedness', label: 'Lucky, not safe', row: 2, col: 1 },
  { key: 'high_exposure_low_preparedness', label: 'Most urgent', row: 2, col: 2 },
] as const;

function quadrantClassName(key: string): string {
  if (key === 'low_exposure_high_preparedness') return 'good';
  if (key === 'high_exposure_low_preparedness') return 'bad';
  return 'mixed';
}

function formatDollars(amount: number): string {
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

function costRangeCopy(low: number, high: number, open: boolean): string {
  if (open) return `${formatDollars(low)}+`;
  if (low === high) return formatDollars(low);
  return `${formatDollars(low)}-${formatDollars(high)}`;
}

const RESOLUTION_COPY: Record<string, string> = {
  same_day: 'the same day',
  few_days: 'a few days',
  '1_4_weeks': '1-4 weeks',
  over_month: 'over a month',
};

function downloadPDF(filename: string, base64: string) {
  const bytes = atob(base64);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);

  const blob = new Blob([buffer], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const RiskReview = () => {
  const [pageState, setPageState] = useState<PageState>('input');
  const [domain, setDomain] = useState('');

  const [scanStatus, setScanStatus] = useState<ScanStatus>('scanning');
  const [scanStepIndex, setScanStepIndex] = useState(0);
  const [scanResult, setScanResult] = useState<RiskReviewResult | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const [answers, setAnswers] = useState<SelfAssessmentAnswers>({});
  const assessmentStarted = useRef(false);
  const assessmentCompleted = useRef(false);

  const [fullReportStatus, setFullReportStatus] = useState<FullReportStatus>('idle');
  const [fullReport, setFullReport] = useState<FullReportResult | null>(null);
  const [fullReportError, setFullReportError] = useState<string | null>(null);

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    trackEvent('risk_review_page_viewed', { referrer: document.referrer || 'direct' });
  }, []);

  useEffect(() => {
    return () => {
      timers.current.forEach(clearTimeout);
    };
  }, []);

  const submitFullReport = async (scan: RiskReviewResult, assessment: SelfAssessmentAnswers) => {
    setFullReportStatus('loading');
    setFullReportError(null);

    try {
      const response = await fetch('/api/v1/risk-review/full-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target: scan.target,
          domain: scan.domain,
          reachable: scan.reachable,
          tier: scan.tier,
          clean: scan.clean,
          authority_score: scan.authority_score ?? 0,
          sms_score: scan.sms_score ?? 0,
          email_score: scan.email_score ?? 0,
          overall_score: scan.overall_score ?? 0,
          named_executives: scan.named_executives,
          staff_with_phone: scan.staff_with_phone,
          staff_with_email: scan.staff_with_email,
          phone_numbers_found: scan.phone_numbers_found,
          email_format_predictable: scan.email_format_predictable,
          assessment,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || 'We ran into a problem building your report.');
      }

      const data: FullReportResult = await response.json();
      setFullReport(data);
      setFullReportStatus('done');
      setPageState('report');
      trackEvent('risk_review_full_report_completed', { target: scan.target, quadrant: data.quadrant });
    } catch (err) {
      setFullReportError(err instanceof Error ? err.message : 'Something went wrong building your report.');
      setFullReportStatus('error');
    }
  };

  // Whichever finishes last — the scan or the questions — triggers the
  // full report automatically. No dependency between the two beyond this.
  useEffect(() => {
    if (scanStatus === 'done' && scanResult && isAssessmentComplete(answers) && fullReportStatus === 'idle') {
      void submitFullReport(scanResult, answers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanStatus, scanResult, answers, fullReportStatus]);

  // PostHog funnel events for the self-assessment step specifically — the
  // surrounding scan/report funnel is already tracked, but nothing today
  // tracks whether a visitor engages with the questions at all. Each
  // fires once per scan session; the refs reset in handleReset (the only
  // place answers is cleared) so a fresh session can fire both again.
  useEffect(() => {
    if (answeredCount(answers) > 0 && !assessmentStarted.current) {
      assessmentStarted.current = true;
      trackEvent('risk_review_self_assessment_started');
    }
    if (isAssessmentComplete(answers) && !assessmentCompleted.current) {
      assessmentCompleted.current = true;
      trackEvent('risk_review_self_assessment_completed');
    }
  }, [answers]);

  const handleScan = async () => {
    if (!domain.trim()) return;

    setPageState('in_progress');
    setScanStatus('scanning');
    setScanStepIndex(0);
    setScanError(null);
    setScanResult(null);
    setFullReportStatus('idle');
    setFullReport(null);
    trackEvent('risk_review_scan_started', { domain: domain.trim() });

    timers.current.forEach(clearTimeout);
    timers.current = STEP_ADVANCE_MS.slice(1).map((ms, i) => setTimeout(() => setScanStepIndex(i + 1), ms));

    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const response = await fetch('/api/v1/risk-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: domain.trim() }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || 'We ran into a problem scanning that site.');
      }

      const data: RiskReviewResult = await response.json();
      timers.current.forEach(clearTimeout);
      setScanStepIndex(STEPS.length);
      trackEvent('risk_review_scan_completed', { target: data.target, tier: data.tier, reachable: data.reachable });

      setScanResult(data);
      setScanStatus('done');
    } catch (err) {
      timers.current.forEach(clearTimeout);
      const message =
        err instanceof Error && err.name === 'AbortError'
          ? 'This scan is taking longer than expected. You can keep answering the questions below while we retry.'
          : err instanceof Error
            ? err.message
            : 'Something went wrong running this scan. Please try again.';
      setScanError(message);
      setScanStatus('error');
      trackEvent('risk_review_scan_failed', { domain: domain.trim() });
    } finally {
      clearTimeout(abortTimer);
    }
  };

  const handleReset = () => {
    setPageState('input');
    setDomain('');
    setScanStatus('scanning');
    setScanResult(null);
    setScanError(null);
    setAnswers({});
    assessmentStarted.current = false;
    assessmentCompleted.current = false;
    setFullReportStatus('idle');
    setFullReport(null);
    setFullReportError(null);
  };

  return (
    <div className="check-page risk-review-page">
      <section className="hero" id="top">
        <h1>Is your company exposed?</h1>
        <p className="lede">
          Enter your company's website — we'll check how easily a scammer could impersonate your leadership or
          target your employees, and pair it with a quick self-assessment for the full picture. Free, and ready in
          under a minute.
        </p>
      </section>

      <div className="console-wrap">
        {pageState === 'input' && (
          <div className="console">
            <div className="console-head">
              <span className="live-dot"></span>
              <span>Check your company</span>
              <span className="sub">— takes about a minute</span>
            </div>
            <div className="console-body" style={{ display: 'block' }}>
              <div className="risk-domain-row">
                <input
                  className="risk-domain-input"
                  placeholder="yourcompany.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                  aria-label="Company website domain"
                />
                <button type="button" className="primary-cta" onClick={handleScan} disabled={!domain.trim()}>
                  Run business risk review
                  <i className="ti ti-arrow-right" aria-hidden="true"></i>
                </button>
              </div>
              <p className="risk-fineprint">
                We only scan your public website. No login, no email required to see results. Part of a larger
                company site? Paste your specific office/team page for the most accurate result.
              </p>
            </div>
          </div>
        )}

        {pageState === 'in_progress' && (
          <>
            <div className="risk-concurrent-layout">
              <div className="risk-concurrent-scan console">
                <div className="console-head">
                  <span>Business Risk Review — {domain.trim()}</span>
                </div>
                <div className="console-body" style={{ display: 'block' }}>
                  {scanStatus === 'scanning' && (
                    <ol className="risk-steps">
                      {STEPS.map((step, i) => {
                        const state = i < scanStepIndex ? 'done' : i === scanStepIndex ? 'active' : 'pending';
                        return (
                          <li className="risk-step" key={step.title}>
                            <span className={`risk-step-icon ${state}`}>
                              {state === 'done' ? <i className="ti ti-check" aria-hidden="true"></i> : i + 1}
                            </span>
                            <div className="risk-step-main">
                              <div className="risk-step-title-row">
                                <span className="risk-step-title">{step.title}</span>
                                <span className={`risk-step-badge ${state}`}>
                                  {state === 'done' ? 'Complete' : state === 'active' ? 'In progress' : 'Pending'}
                                </span>
                              </div>
                              <p className="risk-step-desc">{step.desc}</p>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  {scanStatus === 'done' && (
                    <div className="risk-scan-done-note">
                      <i className="ti ti-circle-check" aria-hidden="true"></i>
                      <span>
                        Scan complete{isAssessmentComplete(answers) ? '' : ' — finish the questions to see your full results.'}
                      </span>
                    </div>
                  )}
                  {scanStatus === 'error' && (
                    <div className="error-message">
                      <i className="ti ti-alert-circle" aria-hidden="true"></i>
                      <span>
                        {scanError}{' '}
                        <button type="button" className="risk-inline-retry" onClick={handleScan}>
                          Retry scan
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="risk-concurrent-assessment console">
                <div className="console-head">
                  <span>Quick self-assessment</span>
                </div>
                <div className="console-body" style={{ display: 'block' }}>
                  <SelfAssessmentForm answers={answers} onChange={setAnswers} />
                </div>
              </div>
            </div>

            {fullReportStatus === 'loading' && (
              <div className="risk-fullreport-loading">
                <span className="verdict-dot"></span>
                Building your full report…
              </div>
            )}
            {fullReportStatus === 'error' && (
              <div className="error-message">
                <i className="ti ti-alert-circle" aria-hidden="true"></i>
                <span>
                  {fullReportError}{' '}
                  {scanResult && (
                    <button
                      type="button"
                      className="risk-inline-retry"
                      onClick={() => submitFullReport(scanResult, answers)}
                    >
                      Retry
                    </button>
                  )}
                </span>
              </div>
            )}
          </>
        )}

        {pageState === 'report' && fullReport && scanResult && (
          <FullReportView report={fullReport} scan={scanResult} onReset={handleReset} />
        )}

        <CtaStrip
          message={
            pageState === 'in_progress'
              ? 'While you finish up — schedule time with our team or drop us a line.'
              : pageState === 'report'
                ? 'Want help closing these gaps? Talk to Everyday Security about protecting your team.'
                : 'Want a deeper look right away? Talk to our team about a full workforce assessment.'
          }
        />
      </div>

      <section className="dark-section" id="trust">
        <div className="dark-inner">
          <div className="badge-icon badge-teal">
            <i className="ti ti-shield-check" aria-hidden="true"></i>
          </div>
          <h2>Your Privacy Matters</h2>
          <p className="body-copy">
            We only look at what's already public on your website. No account, no login, and nothing is shared or
            sold.
          </p>
        </div>
      </section>
    </div>
  );
};

const CtaStrip = ({ message }: { message: string }) => (
  <div className="risk-cta-strip">
    <div className="risk-cta-text">{message}</div>
    <div className="risk-cta-links">
      <a
        className="risk-cta-btn ghost"
        href={`mailto:${CONTACT_EMAIL}`}
        onClick={() => trackEvent('risk_review_cta_clicked', { cta: 'email' })}
      >
        Email us
      </a>
      <a
        className="risk-cta-btn solid"
        href={CALENDLY_URL}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackEvent('risk_review_cta_clicked', { cta: 'schedule_call' })}
      >
        Schedule a call
      </a>
    </div>
  </div>
);

const FullReportView = ({
  report,
  scan,
  onReset,
}: {
  report: FullReportResult;
  scan: RiskReviewResult;
  onReset: () => void;
}) => {
  const tierCopy = TIER_COPY[scan.tier];

  return (
    <div className="console">
      <div className="console-body" style={{ display: 'block', paddingTop: 24 }}>
        <div className="risk-result-head">
          <span className={`risk-tier-pill ${tierCopy.className}`}>{tierCopy.label}</span>
          <span className="risk-result-domain">
            Results for <strong>{report.target}</strong>
          </span>
        </div>

        <h3 className="risk-section-title">Your position</h3>
        <QuadrantGrid active={report.quadrant} />

        {report.perception_gap_note && (
          <div className="risk-perception-note">{report.perception_gap_note}</div>
        )}

        <h3 className="risk-section-title">All 7 sub-scores</h3>
        <div className="risk-subscores">
          {scan.tier !== 'unknown' ? (
            <>
              <SubScoreCard label="Authority exposure" weight="25%" level={exposureLevel(scan.authority_score ?? 0)} desc="Named leadership with contact details is easy to find and impersonate." />
              <SubScoreCard label="SMS targetability" weight="50%" level={exposureLevel(scan.sms_score ?? 0)} desc="Staff phone numbers publicly listed alongside names." />
              <SubScoreCard label="Email targetability" weight="25%" level={exposureLevel(scan.email_score ?? 0)} desc="Staff emails that are discoverable or follow a guessable pattern." />
            </>
          ) : (
            <p className="risk-body-copy">
              We weren't able to reach or scan this website, so the 3 technical exposure scores aren't available
              this time — the self-assessment below still reflects real preparedness regardless.
            </p>
          )}
          <SubScoreCard label="Detection & avoidance" level={preparednessLevel(report.preparedness_a)} desc="How well your team can spot and stop a scam attempt before acting on it." />
          <SubScoreCard label="Incident response" level={preparednessLevel(report.preparedness_b)} desc="How clear your plan is for the first hour after a scam succeeds." />
          <SubScoreCard label="Preventative measures" level={preparednessLevel(report.preparedness_c)} desc="What checks and safeguards you already have in place today." />
          <SubScoreCard label="Incident history" level={preparednessLevel(report.preparedness_d)} desc="How many social engineering incidents you've experienced in the last 12 months." />
        </div>

        {scan.tier !== 'unknown' && (
          <div className="risk-findings">
            <div className="risk-finding-row">
              <span>Named executives identified</span>
              <span className={scan.named_executives > 0 ? 'flag' : 'ok'}>{scan.named_executives}</span>
            </div>
            <div className="risk-finding-row">
              <span>Staff with a phone number listed</span>
              <span className={scan.staff_with_phone > 0 ? 'flag' : 'ok'}>{scan.staff_with_phone}</span>
            </div>
            <div className="risk-finding-row">
              <span>Staff with a discoverable email listed</span>
              <span className={scan.staff_with_email > 0 ? 'flag' : 'ok'}>{scan.staff_with_email}</span>
            </div>
            <div className="risk-finding-row">
              <span>Phone numbers found on the site</span>
              <span className={scan.phone_numbers_found ? 'flag' : 'ok'}>{scan.phone_numbers_found ? 'Yes' : 'No'}</span>
            </div>
            <div className="risk-finding-row">
              <span>Email format appears predictable</span>
              <span className={scan.email_format_predictable ? 'flag' : 'ok'}>
                {scan.email_format_predictable ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        )}

        {(report.roadmap_items?.length ?? 0) > 0 && (
          <>
            <div className="risk-roadmap">
              <h3 className="risk-section-title">Your maturity roadmap</h3>
              {report.roadmap_framing && <p className="risk-roadmap-framing">{report.roadmap_framing}</p>}
              {report.roadmap_items.map((item) => (
                <div key={item.dimension} className="risk-roadmap-item">
                  <h4>{item.title}</h4>
                  <ul>
                    {item.steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="risk-fundamentals">
              <h3 className="risk-section-title">The fundamentals</h3>
              <p className="risk-fundamentals-intro">Whatever your specific gaps, these three matter for every business:</p>
              {report.baseline_advocacy.map((category) => (
                <div key={category.title} className="risk-fundamentals-item">
                  <h4>{category.title}</h4>
                  <p>{category.desc}</p>
                </div>
              ))}
              <p className="risk-fundamentals-outro">If you'd like to try any of these, get in touch below.</p>
            </div>
          </>
        )}

        <div className="risk-cost-estimate">
          <h3 className="risk-section-title">What this could cost</h3>
          {report.cost_has_history ? (
            <p>
              Based on what actually happened, and what you estimate that kind of incident tends to cost you, social
              engineering incidents may have cost your organization roughly{' '}
              <strong>{costRangeCopy(report.cost_total_low ?? 0, report.cost_total_high ?? 0, !!report.cost_total_open)}</strong>{' '}
              over the last 12 months. You also told us a successful scam would likely take{' '}
              {RESOLUTION_COPY[report.resolution_time] ?? 'some time'} to fully detect and resolve — the longer that
              takes, the more this cost compounds.
            </p>
          ) : (
            <p>
              You told us this hasn't happened yet — and based on your own estimate, a single incident could cost
              your organization in the{' '}
              <strong>{costRangeCopy(report.cost_single_low ?? 0, report.cost_single_high ?? 0, !!report.cost_single_open)}</strong>{' '}
              range, potentially taking {RESOLUTION_COPY[report.resolution_time] ?? 'some time'} to fully detect and
              resolve. That's exactly why staying in this position is worth protecting.
            </p>
          )}
          <p className="risk-cost-caveat">This is a rough, self-reported range for illustration - not a guarantee.</p>
        </div>

        <div className="risk-pdf-cta">
          <div>
            <h3>Your full report is ready</h3>
            <p>Download the PDF for the complete breakdown — nothing here reveals the specific names, emails, or numbers we found.</p>
          </div>
          <button
            type="button"
            className="risk-pdf-btn"
            onClick={() => {
              downloadPDF(report.report_filename, report.report_pdf_base64);
              trackEvent('risk_review_pdf_downloaded', { target: report.target, quadrant: report.quadrant });
            }}
          >
            <i className="ti ti-download" aria-hidden="true"></i>
            Download PDF report
          </button>
        </div>

        <button type="button" className="risk-scan-another" onClick={onReset}>
          Check another company
        </button>
      </div>
    </div>
  );
};

const QuadrantGrid = ({ active }: { active: string }) => (
  <div className="quadrant-wrap">
    <div className="quadrant-grid">
      {QUADRANTS.map((q) => (
        <div
          key={q.key}
          className={`quadrant-cell ${quadrantClassName(q.key)}${q.key === active ? ' active' : ''}`}
          style={{ gridRow: q.row, gridColumn: q.col }}
        >
          <span className="quadrant-cell-title">{q.label}</span>
          {q.key === active && <span className="quadrant-cell-marker">You are here</span>}
        </div>
      ))}
    </div>
    <p className="quadrant-axis-caption">
      Left to right: Exposure (low to high) · Bottom to top: Preparedness (low to high)
    </p>
  </div>
);

const SubScoreCard = ({
  label,
  weight,
  level,
  desc,
}: {
  label: string;
  weight?: string;
  level: { label: string; className: string };
  desc: string;
}) => (
  <div className="risk-subscore-card">
    <div className="risk-subscore-label">
      {label} {weight && <span className="weight">· {weight}</span>}
    </div>
    <div className={`risk-subscore-value ${level.className}`}>{level.label}</div>
    <p className="risk-subscore-desc">{desc}</p>
  </div>
);
